import { onSnapshot, setDoc, updateDoc } from 'firebase/firestore'
import { appSettingsRef, iterationsRef, taxonomyRef } from './paths'
import { DEFAULT_APP_SETTINGS, DEFAULT_TAXONOMY } from './defaults'
import { normaliseTaxonomy, toStoredTaxonomy } from './taxonomy'
import { DEFAULT_ITERATIONS, normaliseIterations, type IterationConfig } from './iterations'
import type { AppSettings, Category, StoredTaxonomy, Taxonomy } from './types'

export interface SettingsSnapshot<T> {
  value: T | null
  /** True once the server has confirmed the document is genuinely absent. */
  confirmedMissing: boolean
}

export function subscribeTaxonomy(
  uid: string,
  onChange: (snapshot: SettingsSnapshot<Taxonomy>) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    taxonomyRef(uid),
    (doc) =>
      onChange({
        value: doc.exists() ? normaliseTaxonomy(doc.data() as StoredTaxonomy) : null,
        confirmedMissing: !doc.exists() && !doc.metadata.fromCache,
      }),
    (error) => onError?.(error),
  )
}

export function subscribeAppSettings(
  uid: string,
  onChange: (snapshot: SettingsSnapshot<AppSettings>) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    appSettingsRef(uid),
    (doc) =>
      onChange({
        value: doc.exists() ? { ...DEFAULT_APP_SETTINGS, ...doc.data() } : null,
        confirmedMissing: !doc.exists() && !doc.metadata.fromCache,
      }),
    (error) => onError?.(error),
  )
}

/**
 * Seeds the default taxonomy and settings. Only ever called once the server has
 * confirmed the documents are missing, so a cold cache offline can't overwrite a
 * taxonomy that already exists.
 */
export async function seedTaxonomy(uid: string): Promise<void> {
  await setDoc(taxonomyRef(uid), toStoredTaxonomy(DEFAULT_TAXONOMY) as never)
}

export async function seedAppSettings(uid: string): Promise<void> {
  await setDoc(appSettingsRef(uid), DEFAULT_APP_SETTINGS)
}

export async function updateAppSettings(uid: string, patch: Partial<AppSettings>): Promise<void> {
  await updateDoc(appSettingsRef(uid), patch)
}

export async function writeTaxonomy(uid: string, taxonomy: Taxonomy): Promise<void> {
  await setDoc(taxonomyRef(uid), toStoredTaxonomy(taxonomy) as never)
}

export function categoryMap(taxonomy: Taxonomy | null): Map<string, Category> {
  return new Map((taxonomy?.categories ?? []).map((category) => [category.id, category]))
}

/** Categories still on the grid, in layout order. Archived ones stay resolvable by id. */
export function activeCategories(taxonomy: Taxonomy | null): Category[] {
  return (taxonomy?.categories ?? []).filter((category) => !category.archived)
}

export function subscribeIterations(
  uid: string,
  onChange: (config: IterationConfig) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    iterationsRef(uid),
    (doc) => onChange(doc.exists() ? normaliseIterations(doc.data()) : DEFAULT_ITERATIONS),
    (error) => onError?.(error),
  )
}

export async function writeIterations(uid: string, config: IterationConfig): Promise<void> {
  await setDoc(iterationsRef(uid), config)
}
